import React from "react";
import { Box, Heading, Hidden, Text, TileLink } from "@origin-digital/ods-core";
import { IconChevronRightSmall } from "@origin-digital/ods-icons";

interface TileButtonProps {
  title: string;
  description?: string;
  imageUrl: string;
  imageAlt?: string;
  dataId: string;
  href?: string;
  handleTileClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const TileContent: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => (
  <Box
    display="flex"
    gap="small"
    flexDirection="column"
    width="full"
    paddingY={{ xs: "xlarge", lg: "xlarge" }}
  >
    {/* Title */}
    <Box display="flex" gap="xsmall" alignItems="center">
      <Heading variant="h3">{title}</Heading>
      <IconChevronRightSmall
        variant="solid"
        color="neutralDark"
        size="xsmall"
      />
    </Box>
    {description && <Text tone="neutralDark">{description}</Text>}
  </Box>
);

const TileImage: React.FC<{ imageUrl: string; imageAlt: string }> = ({
  imageUrl,
  imageAlt,
}) => (
  <>
    {/* Image XS */}
    <Hidden above="sm">
      <Box display="flex" style={{ width: "64px" }} overflow="hidden">
        <img src={imageUrl} alt={imageAlt} height="96" />
      </Box>
    </Hidden>
    {/* Image SM-XL */}
    <Hidden below="sm">
      <Box display="flex">
        <img src={imageUrl} alt={imageAlt} height="96" />
      </Box>
    </Hidden>
  </>
);

export const TileButton: React.FC<TileButtonProps> = ({
  title,
  description,
  handleTileClick,
  imageUrl,
  imageAlt = "",
  dataId,
  href,
}) => {
  return (
    <TileLink
      dataId={dataId}
      cardProps={{
        borderRadius: "medium",
        paddingXSpecific: "none",
        paddingYSpecific: "medium",
        height: "full",
        outline: true,
        onClick: href ? undefined : handleTileClick,
      }}
      hideChevron
      href={href || "javascript:void(0)"}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "row" }}
        gap={{ xs: "large", sm: "xlarge" }}
        paddingLeft={{ xs: "large", sm: "xlarge" }}
        paddingRight={{ xs: "none", sm: "medium" }}
        width="full"
        height="full"
        alignItems="center"
      >
        {/* Body */}
        <TileContent title={title} description={description} />
        {/* Image */}
        <TileImage imageUrl={imageUrl} imageAlt={imageAlt} />
      </Box>
    </TileLink>
  );
};
